/**
 * Audit logs list page.
 * 
 * @module pages/AuditLogsPage
 * @see CSDEV-142 - Audit logs page: add filters for admin, resource type, and date range
 */

import { useState, useEffect } from 'react';
import { DataTable, Pagination, Badge, Card } from '../components/ui';
import { useAuditLogs, useAuditActionTypes, useAuditResourceTypes, useAuditAdmins, usePagination, useAuditLog } from '../hooks';
import { AuditActionTypes } from '../lib/audit';
import { formatDateTime } from '../lib/utils';
import type { AdminAuditLog, DataTableColumn } from '../types';

/**
 * Audit logs list page component.
 * 
 * @returns Audit logs page element
 */
export function AuditLogsPage() {
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  const { page, pageSize, goToPage, changePageSize } = usePagination({
    initialPageSize: 50,
  });
  const { logAction } = useAuditLog();

  const { data: actionTypes } = useAuditActionTypes();
  const { data: resourceTypes } = useAuditResourceTypes();
  const { data: admins } = useAuditAdmins();
  const { data, isLoading, error } = useAuditLogs({
    page,
    pageSize,
    actionType: actionTypeFilter || undefined,
    adminUserId: adminFilter || undefined,
    resourceType: resourceTypeFilter || undefined,
    dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
    dateTo: dateTo ? new Date(dateTo + 'T23:59:59').toISOString() : undefined,
    sortBy,
    sortOrder,
  });

  /**
   * Handles column sort clicks.
   * Toggles order if same column, resets to desc for new column.
   * @see CSDEV-147 - Wire up DataTable sorting
   */
  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('desc');
    }
    goToPage(1);
  };

  /**
   * Clears all filters and resets to first page.
   */
  const clearFilters = () => {
    setActionTypeFilter('');
    setAdminFilter('');
    setResourceTypeFilter('');
    setDateFrom('');
    setDateTo('');
    goToPage(1);
  };

  const hasActiveFilters = actionTypeFilter || adminFilter || resourceTypeFilter || dateFrom || dateTo;

  // Log page view
  useEffect(() => {
    logAction(AuditActionTypes.VIEW_AUDIT_LOGS);
  }, [logAction]);

  const getActionBadgeVariant = (action: string): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
    if (action.includes('login')) return 'success';
    if (action.includes('search')) return 'warning';
    if (action.includes('view')) return 'info';
    return 'neutral';
  };

  const columns: DataTableColumn<AdminAuditLog>[] = [
    {
      key: 'created_at',
      header: 'Timestamp',
      sortable: true,
      render: (log) => (
        <span className="text-gray-500 text-sm font-mono">
          {formatDateTime(log.created_at)}
        </span>
      ),
    },
    {
      key: 'admin_email',
      header: 'Admin',
      render: (log) => (
        <span className="font-medium text-gray-900">{log.admin_email}</span>
      ),
    },
    {
      key: 'action_type',
      header: 'Action',
      render: (log) => (
        <Badge variant={getActionBadgeVariant(log.action_type)} size="sm">
          {log.action_type.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'resource_type',
      header: 'Resource Type',
      render: (log) => (
        <span className="text-gray-600">{log.resource_type || '—'}</span>
      ),
    },
    {
      key: 'resource_details',
      header: 'Details',
      render: (log) => {
        if (!log.resource_details || typeof log.resource_details !== 'object' || Array.isArray(log.resource_details)) {
          return '—';
        }
        const details = log.resource_details as Record<string, unknown>;
        // Show first meaningful detail
        const firstKey = Object.keys(details)[0];
        if (!firstKey) return '—';
        return (
          <span className="text-gray-500 text-sm truncate block max-w-xs">
            {firstKey}: {String(details[firstKey])}
          </span>
        );
      },
    },
    {
      key: 'user_agent',
      header: 'Browser',
      render: (log) => {
        if (!log.user_agent) return '—';
        // Extract browser name from user agent
        const ua = log.user_agent;
        let browser = 'Unknown';
        if (ua.includes('Chrome')) browser = 'Chrome';
        else if (ua.includes('Firefox')) browser = 'Firefox';
        else if (ua.includes('Safari')) browser = 'Safari';
        else if (ua.includes('Edge')) browser = 'Edge';
        return <span className="text-gray-500 text-sm">{browser}</span>;
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="mt-1 text-gray-500">
          View all admin actions and access logs for compliance
        </p>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Admin filter */}
            <select
              value={adminFilter}
              onChange={(e) => {
                setAdminFilter(e.target.value);
                goToPage(1);
              }}
              className="input w-full sm:w-56"
            >
              <option value="">All admins</option>
              {admins?.map((admin) => (
                <option key={admin.id} value={admin.id}>
                  {admin.email}
                </option>
              ))}
            </select>

            {/* Action type filter */}
            <select
              value={actionTypeFilter}
              onChange={(e) => {
                setActionTypeFilter(e.target.value);
                goToPage(1);
              }}
              className="input w-full sm:w-48"
            >
              <option value="">All actions</option>
              {actionTypes?.map((action) => (
                <option key={action} value={action}>
                  {action.replace(/_/g, ' ')}
                </option>
              ))}
            </select>

            {/* Resource type filter */}
            <select
              value={resourceTypeFilter}
              onChange={(e) => {
                setResourceTypeFilter(e.target.value);
                goToPage(1);
              }}
              className="input w-full sm:w-48"
            >
              <option value="">All resources</option>
              {resourceTypes?.map((type) => (
                <option key={type} value={type}>
                  {type.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          {/* Date range filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="dateFrom" className="text-sm text-gray-600 whitespace-nowrap">From:</label>
              <input
                type="date"
                id="dateFrom"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  goToPage(1);
                }}
                className="input w-full sm:w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="dateTo" className="text-sm text-gray-600 whitespace-nowrap">To:</label>
              <input
                type="date"
                id="dateTo"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  goToPage(1);
                }}
                className="input w-full sm:w-40"
              />
            </div>

            {/* Clear filters button */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="btn-secondary text-sm"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{data?.count || 0}</p>
          <p className="text-sm text-gray-500">Total Logs</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-success-600">
            {data?.data?.filter((l) => l.action_type.includes('login')).length || 0}
          </p>
          <p className="text-sm text-gray-500">Logins (this page)</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-info-600">
            {data?.data?.filter((l) => l.action_type.includes('view')).length || 0}
          </p>
          <p className="text-sm text-gray-500">Views (this page)</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-warning-600">
            {data?.data?.filter((l) => l.action_type.includes('search')).length || 0}
          </p>
          <p className="text-sm text-gray-500">Searches (this page)</p>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <Card>
          <p className="text-error-600">Failed to load audit logs: {error.message}</p>
        </Card>
      )}

      {/* Data table */}
      <Card padding="none">
        <DataTable
          columns={columns}
          data={data?.data || []}
          isLoading={isLoading}
          emptyMessage="No audit logs found"
          keyExtractor={(log) => log.id}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
        {data && data.totalPages > 0 && (
          <Pagination
            currentPage={page}
            totalPages={data.totalPages}
            onPageChange={goToPage}
            pageSize={pageSize}
            onPageSizeChange={changePageSize}
            totalItems={data.count}
            pageSizeOptions={[25, 50, 100]}
          />
        )}
      </Card>
    </div>
  );
}
